// The popover anchor a mapped target gets: poster, play icon and the
// `data-pix-*` attributes popover.js reads.
import { preloadImage } from "@/modules/assetImageCache";
import { isLocalPlayHost } from "@/mapping/experiencePrefetch";
import { pickIconDims } from "@/mapping/playIcon";
import type { ExperiencePublishData } from "@/types/experience";

type PublishData = ExperiencePublishData | null | undefined;

/** The runner's overrides buildAnchor reads, called where they were read before. */
export interface AnchorRunner {
  getPlayServerHost: () => string;
  getPlayerUrlOverride: () => string;
  getTargetWindowOverride: () => string;
}

export function buildAnchor(
  runner: AnchorRunner,
  pageUrl: string,
  posterSrc: string,
  publishData: PublishData,
): HTMLAnchorElement {
  const data: ExperiencePublishData = publishData || {};
  const anchor = document.createElement("a");
  anchor.className = "pixmoto-popover-anchor";
  anchor.setAttribute(
    "style",
    "border-style: none; position: absolute; top: 0; left: 0; display: block; line-height: 0; width: 100%; height: 100%; max-width: 100%; max-height: 100%; overflow: hidden;",
  );

  // When the API resolves an experience id, address the player by
  // id directly (`data-pix-expid`) — popover.js will fetch
  // `/publisher/experience/{id}` instead of re-resolving the page
  // URL. Fall back to URL resolution if the API didn't return one.
  anchor.setAttribute("data-pix-url", pageUrl);
  if (data.idexperience != null) {
    anchor.setAttribute("data-pix-expid", String(data.idexperience));
  } else {
    anchor.setAttribute("data-idexperience", "");
  }

  anchor.setAttribute("data-pix-autoplay", "true");

  if (data.view) {
    anchor.setAttribute("data-pix-dsw", String(data.view));
  }

  const dims = pickIconDims(data);
  anchor.setAttribute("data-pix-pis", `${dims.width}x${dims.height}`);

  if (data.campaign) {
    anchor.setAttribute("data-pix-cid", String(data.campaign));
  }

  // Pin overlay iframe to the play host that serves popover.js (local
  // preview uses localhost:4000). Matches preview-desktop-full.html.
  const playHost = runner.getPlayServerHost();
  if (playHost) {
    anchor.setAttribute("data-pix-penv", playHost);
  }

  // `hostUrl` is a publisher-domain delegate page (embed.js). Skip on
  // local play hosts so `autoplay` and other query params reach the
  // player directly instead of through a cross-origin delegate hop.
  const hostUrl = data.hostUrl || runner.getPlayerUrlOverride();
  if (hostUrl && !isLocalPlayHost(playHost)) {
    anchor.setAttribute("data-pix-hu", hostUrl);
  }

  // `mobileTarget` controls Same-Tab / New-Tab behavior on mobile
  // (BannerOptions "Target Tab" radio). API value wins; script-tag
  // override is the fallback.
  const targetWindow = data.mobileTarget || runner.getTargetWindowOverride();
  if (targetWindow) {
    anchor.setAttribute("data-pix-win", targetWindow);
  }

  anchor.setAttribute("href", "#");

  const img = document.createElement("img");
  img.setAttribute("decoding", "async");
  img.setAttribute("src", posterSrc);
  img.setAttribute("width", "100%");
  img.setAttribute("height", "100%");
  img.className = "pixmoto-mapped-poster";
  img.setAttribute(
    "style",
    "display: block; width: 100%; max-width: 100%; height: 100%; max-height: 100%; object-fit: cover; position: absolute; left: 0; top: 0;",
  );
  anchor.appendChild(img);

  // Inject our own `.pixmoto-mapped-play-icon` so the icon is
  // present immediately (no wait for popover.js + image preload),
  // and so popover.js will find it via
  // `anchor.querySelector('.pixmoto-mapped-play-icon')` and reuse
  // it instead of creating a second one. Popover.js will later
  // write `width / height / left / bottom / opacity` to this
  // element via `el.style.foo = ...` — those plain DOM writes
  // silently strip any prior `!important` flag (per CSSOM spec
  // for the IDL setter), so we can't just set `!important` once
  // and walk away. We center via `top/left/right/bottom: 0 +
  // margin: auto` (auto-margin trick — survives ancestor
  // `transform`s set by Squarespace animation classes that
  // would break a `transform: translate(-50%, -50%)` approach)
  // and a `MutationObserver` (set up in `forceShowPixmotoAnchor`)
  // re-applies our `!important` styles whenever popover writes.
  const playIcon = document.createElement("img");
  playIcon.className = "pixmoto-mapped-play-icon";
  const defaultPlayIconSrc = "//cdn.pixmoto.com/play/pixmoto-play-icon.png";
  preloadImage(defaultPlayIconSrc).then(() => {
    if (!playIcon.getAttribute("src")) {
      playIcon.setAttribute("src", defaultPlayIconSrc);
    }
  });
  playIcon.setAttribute("width", String(dims.width));
  playIcon.setAttribute("height", String(dims.height));
  // Stashed on the element so `applyPlayIconStyles` can read the
  // authoritative size whenever it re-applies `!important` styles
  // after popover.js mutates `style`.
  playIcon.dataset.pixmotoIconWidth = String(dims.width);
  playIcon.dataset.pixmotoIconHeight = String(dims.height);
  anchor.appendChild(playIcon);

  return anchor;
}
